import { ArrowLeft, MapPin, Plus, Trash2, AlertCircle, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useBeneficiaries, useDeleteBeneficiary } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import { logger } from '@/utils/logger'

interface BeneficiariesManagementScreenProps {
  onBack: () => void
  onEditBeneficiary: (beneficiary: CpnBeneficiaryUser) => void
  onAddBeneficiary: () => void
  userId: string
}

const BeneficiariesManagementScreen = ({
  onBack,
  onEditBeneficiary,
  onAddBeneficiary,
  userId,
}: BeneficiariesManagementScreenProps) => {
  const [beneficiaryToDelete, setBeneficiaryToDelete] =
    useState<CpnBeneficiaryUser | null>(null)

  // Helper function to generate initials from first and last name
  const getInitials = (firstName: string, lastName: string): string => {
    const firstInitial = firstName?.charAt(0)?.toUpperCase() || ''
    const lastInitial = lastName?.charAt(0)?.toUpperCase() || ''
    return firstInitial + lastInitial
  }

  const { data: beneficiaries, isLoading, error } = useBeneficiaries(userId)
  const deleteBeneficiaryMutation = useDeleteBeneficiary()

  const handleDeleteClick = (beneficiary: CpnBeneficiaryUser) => {
    setBeneficiaryToDelete(beneficiary)
  }

  const handleConfirmDelete = async () => {
    if (!beneficiaryToDelete) return

    try {
      await deleteBeneficiaryMutation.mutateAsync({
        beneficiaryId: beneficiaryToDelete.uuid,
      })
      setBeneficiaryToDelete(null)
      // No need to manually refresh - TanStack Query will automatically update the UI
    } catch (error) {
      logger.error('Failed to delete Recipient', error)
      alert('Failed to delete Recipient. Please try again.')
    }
  }

  const handleCancelDelete = () => {
    setBeneficiaryToDelete(null)
  }

  return (
    <div className="flex flex-col h-[600px] dark:bg-gray-900">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="p-1 flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Manage Recipients
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                View and edit your recipients
              </p>
            </div>
          </div>

          {/* Show compact button only when there are beneficiaries */}
          {!isLoading &&
            !error &&
            beneficiaries &&
            beneficiaries.beneficiaryUsers.length > 0 && (
              <Button
                onClick={onAddBeneficiary}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex-shrink-0 h-12 sm:px-3 px-0 min-w-[48px]"
                size="sm"
              >
                <span className="hidden sm:flex sm:items-center">
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="text-xs font-medium">Add Recipient</span>
                </span>
                <span className="sm:hidden text-lg font-medium">+</span>
              </Button>
            )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-1"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card className="p-4 bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-700">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Failed to load beneficiaries</span>
            </div>
          </Card>
        )}

        {/* Beneficiaries List */}
        {!isLoading && !error && beneficiaries && (
          <div className="space-y-3">
            {beneficiaries.beneficiaryUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px]">
                <Card className="p-8 text-center mb-6">
                  <Plus className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                    No beneficiaries yet
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Add your first recipient to get started
                  </p>
                </Card>

                <Button
                  onClick={onAddBeneficiary}
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-14 px-4 text-sm font-semibold shadow-lg"
                  size="sm"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Your First Recipient
                </Button>
              </div>
            ) : (
              beneficiaries.beneficiaryUsers.map(
                (beneficiary: CpnBeneficiaryUser) => (
                  <Card
                    key={beneficiary.uuid}
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => onEditBeneficiary(beneficiary)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {getInitials(
                              beneficiary.firstName,
                              beneficiary.lastName
                            )}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                            {beneficiary.firstName} {beneficiary.lastName}
                          </h3>

                          <div className="flex items-center space-x-1 text-xs text-gray-600 dark:text-gray-400 mt-1">
                            <MapPin className="w-4 h-3 flex-shrink-0" />
                            <span className="truncate">
                              {beneficiary.city || 'N/A'},{' '}
                              {beneficiary.country || 'N/A'}
                            </span>
                          </div>

                          {beneficiary.street && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                              {beneficiary.street}
                              {beneficiary.stateProvince &&
                                `, ${beneficiary.stateProvince}`}
                              {beneficiary.postalCode &&
                                ` ${beneficiary.postalCode}`}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteClick(beneficiary)
                          }}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900"
                          title="Delete Recipient"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                )
              )
            )}
          </div>
        )}
      </div>

      {/* Deletion Confirmation Overlay */}
      {beneficiaryToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-sm mx-4 w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete Recipient
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelDelete}
                className="p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-sm">
                {beneficiaryToDelete.firstName} {beneficiaryToDelete.lastName}
              </span>
              ? This action cannot be undone.
            </p>

            <div className="flex space-x-2 text-sm">
              <Button
                variant="outline"
                onClick={handleCancelDelete}
                className="flex-1 text-sm"
                disabled={deleteBeneficiaryMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm"
                disabled={deleteBeneficiaryMutation.isPending}
              >
                {deleteBeneficiaryMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BeneficiariesManagementScreen
